import { FC, useEffect, useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import { AlertTitle, Button, Skeleton } from "@mui/material";
import Paper from "@mui/material/Paper";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
	AddCustomQuoteAlert,
	AddCustomQuoteWrapper,
	AddQuoteLineItemGridContainer,
	AttributesContainer,
	BaseItemEdit,
	ChargesContainer,
	Content,
	ErrorText,
	Heading,
	ItemAndModsContainer,
	RequiredFieldWrapper,
	SelectedBox,
	SendQuoteButton,
	SendQuoteButtonsContainer,
	StyledAddLineItem
} from "pages/CustomQuote/AddQuotePage/addQuotePage.styles.ts";
import { GlobalInfo } from "components/OrderDetailsLineItem/OrderDetailsLineItem.styles.ts";
import {
	CustomQuoteItemDetailField,
	CustomQuoteItemDetailTitle,
	CustomQuoteLineItemGrid
} from "components/CustomQuote/CustomQuoteDetail/CustomQuoteDetail.styles.ts";
import { ModificationSkeletonContainer } from "components/NewOrders/AddModification/AddModificationStyles.ts";
import { LinkButton } from "components/Common/Link";
import HelperText from "components/Form/HelperText/HelperText.tsx";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner.tsx";
import AddGlobalAttributes from "components/NewOrders/AddGlobalAttributes/AddGlobalAttributes.tsx";
import GlobalFormProvider from "components/NewOrders/AddGlobalAttributes/forms/GlobalFormProvider.tsx";
import { CustomQuoteDetail } from "components/CustomQuote/CustomQuoteDetail/CustomQuoteDetail.tsx";
import { AutocadUpload } from "components/CustomQuote/AutocadUpload/AutocadUpload.tsx";
import { Header, Modification } from "components/Common/LineItemGrid";
import {
	AddCustomQuoteText,
	BaseItemHeaderText,
	BaseItemListPriceText,
	CantProcessRequestText,
	CustomizationChargeText,
	CustomQuoteRequest,
	CustomQuoteRequestText,
	DoorStyleText,
	DWGDownloadText,
	DWGErrorText,
	DwgPdfBothRequiredErrorText,
	EditedText,
	FinishText,
	GenerateBasePriceText,
	GeneratingDWGText,
	ManualFlatAdjustment,
	ManualPercentAdjustment,
	RequiredFieldsText,
	ServerTemporarilyUnavailableText,
	StandardListPriceText,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import { SendQuoteButtonText, TotalPriceText } from "components/NewOrders/constants.ts";
import { CQCustomizationsHeaderText } from "components/NewOrders/AddModification/constants.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import {
	getAttributes,
	getSubheaderAttributes,
	handlePriceValidationErrorMessaging
} from "components/NewOrders/utils/NewOrderUtils.tsx";
import { formatPriceWithPrefix } from "utils/string.ts";
import {
	calculateCustomizationChargeAmount,
	calculateManualPercentAdjustmentAmount,
	calculateQuoteTotal,
	sanitizeFlatAdjustment,
	sanitizePrefix
} from "pages/CustomQuote/AddQuotePage/upchargeUtils.ts";
import { uploadFile, validateDwgPdfFiles } from "pages/CustomQuote/AddQuotePage/utils/fileUtils.ts";
import { useDialog } from "hooks/useDialog.ts";
import useDownloadAutoCadFile from "hooks/useDownloadAutoCadFile.ts";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";
import { useGeneratePriceEstimate } from "hooks/useGeneratePriceEstimate.ts";
import useBlobStorage from "hooks/useBlobStorage.ts";
import { useRevisions } from "hooks/useRevisions.ts";
import { useAddQuoteState } from "pages/CustomQuote/AddQuotePage/hooks/useAddQuoteState.ts";
import { useGetSpecialModificationsQuery } from "features/api/newOrderApi.ts";
import { useGetCustomQuoteByIdQuery, useUpdateRevisionMutation } from "features/api/customQuotesApi.ts";
import { useLazyGetCqComplexityTierConfigQuery } from "features/api/configurationsApi.ts";
import { models } from "types/api/viewModels.ts";
import { ReplacementFile } from "features/reducers/replacementOrder/cart.ts";
import useCqComplexityScores from "pages/CustomQuote/AddQuotePage/hooks/useCqComplexityScores.ts";
import QuoteCustomizationsSummary from "components/CustomQuote/QuoteCustomizationsSummary.tsx";
import { CQ_CODES } from "pages/CustomQuote/customQuoteModificationCodes.ts";
import { CQModifications } from "pages/CustomQuote/AddQuotePage/components/CQModifications/CQModifications.tsx";
import { CQModificationsFormFields } from "pages/CustomQuote/AddQuotePage/components/CQModifications/forms/CQModificationsFormSchema.ts";

export const getCqParentLineItemBySku = (
	sku: string | null | undefined
): models["CqModLineItemViewModel"] | undefined => {
	const selectedCode = CQ_CODES.find((cqCode) => cqCode.sku === sku);
	if (!selectedCode) {
		return undefined;
	}

	return {
		id: selectedCode.productId,
		sku: selectedCode.sku
	};
};

const AddQuotePage: FC = () => {
	const { quoteId } = useParams();
	const [searchParams] = useSearchParams();
	const revisionId = Number(searchParams.get("revisionId") ?? "0");
	const navigate = useNavigate();

	const { data: specialModifications } = useGetSpecialModificationsQuery();
	const {
		data: customQuoteData,
		isLoading: isLoadingCustomQuote,
		isFetching: isFetchingCustomQuote
	} = useGetCustomQuoteByIdQuery(quoteId ?? "");
	const [updateRevision, { error: updateRevisionError }] = useUpdateRevisionMutation();
	const [fetchCqComplexityTierConfig, { data: complexityTierConfig }] = useLazyGetCqComplexityTierConfigQuery();

	const { openDialog } = useDialog();
	const { deleteRevisionError } = useRevisions();
	const { dwgData, generateAutoCadFile } = useDownloadAutoCadFile(quoteId, revisionId);
	const { snackbarOpen } = useSnackbarEffect(dwgData.loading);
	const { uploadContainerBlob } = useBlobStorage();
	const [isBaseItemLoading, setIsBaseItemLoading] = useState(false);

	const [revision, setRevision] = useState<models["CustomQuoteRevisionViewModel"]>();

	const handleSetRevisionGlobals = (globals: models["GlobalsViewModel"] | undefined) => {
		setRevision((prev) => (prev ? { ...prev, globals } : prev));
	};

	const handleSetRevisionLineItem = (lineItem: models["PendingLineItemViewModel"] | undefined) => {
		setRevision((prev) => (prev ? { ...prev, lineItem } : prev));
	};

	const [isRevisionOfRevision, setIsRevisionOfRevision] = useState<boolean>(false);
	const {
		lineItemMode,
		setLineItemMode,
		total,
		setTotal,
		invalidUploads,
		setInvalidUploads,
		uploadedFiles,
		setUploadedFiles,
		isLoading,
		setIsLoading
	} = useAddQuoteState();

	const [cqFormValues, setCqFormValues] = useState<CQModificationsFormFields | null>(null);

	const manualPercentAdjustment = cqFormValues?.manualPercentAdjustment ?? "";
	const manualFlatAdjustment = cqFormValues?.manualFlatAdjustment ?? "";
	const cqComplexityScoreTotal = useCqComplexityScores({
		cqMods: cqFormValues?.cqMods ?? revision?.cqLineItem?.cqMods ?? []
	});
	const isBaseOrRevise = lineItemMode === "base" || lineItemMode === "revise";
	const isOtherOrRevise = lineItemMode === "other" || lineItemMode === "revise";
	const isSpecialMode = lineItemMode === "special";
	const isOtherMode = lineItemMode === "other";

	useEffect(() => {
		if (!customQuoteData) return;

		const foundRevision = customQuoteData.revisions?.find((rev) => rev.revisionId === revisionId);

		if (!foundRevision) return;

		const revisingRevision = !!foundRevision?.sourceId;
		setIsRevisionOfRevision(revisingRevision);
		setRevision((prev) => {
			if (prev) return prev;
			if (revisingRevision) {
				setLineItemMode("revise");
			}
			return {
				...foundRevision,
				lineItem: foundRevision?.lineItem ?? customQuoteData.lineItem,
				globals: foundRevision?.globals ?? customQuoteData.globals
			};
		});
		setCqFormValues({
			modNotes: foundRevision?.notes ?? "",
			specialMods: foundRevision?.specialMods ?? [],
			cqCode: foundRevision?.cqLineItem?.sku ?? "",
			cqMods: foundRevision?.cqLineItem?.cqMods ?? [],
			manualPercentAdjustment: foundRevision?.manualPercentageCharge
				? String(foundRevision.manualPercentageCharge)
				: "",
			manualFlatAdjustment: foundRevision?.manualFlatFeeCharge ? String(foundRevision.manualFlatFeeCharge) : ""
		});
	}, [customQuoteData, revisionId, setLineItemMode]);

	const {
		postError,
		lineItemErrors,
		errorBannerType,
		isValidateLoading,
		handleGeneratePriceEstimate,
		addedLineItemWithPrice,
		addedGlobals,
		extendedPrice,
		lineItemImportErrors,
		errorBannerElement
	} = useGeneratePriceEstimate({
		setLineItemMode,
		cqData: customQuoteData,
		isRevisionOfRevision
	});

	const baseQuoteTotal = extendedPrice ?? revision?.price ?? 0;

	const manualPercentAdjustmentAmount = calculateManualPercentAdjustmentAmount(
		baseQuoteTotal,
		manualPercentAdjustment
	);
	const manualFlatAdjustmentAmount = sanitizeFlatAdjustment(manualFlatAdjustment);
	const customizationChargeAmount = calculateCustomizationChargeAmount(
		baseQuoteTotal,
		cqFormValues?.specialMods ?? revision?.specialMods ?? [],
		cqComplexityScoreTotal,
		complexityTierConfig
	);

	const handleFilesSelected = (files: ReplacementFile[]) => {
		setUploadedFiles(files);
	};

	const findCqCode = (cqString: string | null | undefined, cqMods?: models["CqModViewModel"][]) => {
		const cqCode = cqString?.split(" - ");
		const foundCq = CQ_CODES.find((code) => code.sku === cqCode?.[0]);
		const cqLineItem: models["CqModLineItemViewModel"] = {
			id: foundCq?.productId,
			sku: foundCq?.sku,
			cqMods: cqMods
		};
		return cqLineItem;
	};

	const getAutoCADFile = async () => {
		if (!quoteId || !cqFormValues) return;

		const { modNotes: notes, specialMods, cqCode, cqMods } = cqFormValues;
		const cqLineItem = findCqCode(cqCode, cqMods ?? []);

		try {
			const response = await updateRevision({
				quoteId,
				revisionId,
				revision: {
					dwgFileUrl: null,
					lineItem: revision?.lineItem,
					specialMods,
					cqLineItem,
					notes,
					price: total ?? 0,
					manualPercentageCharge: sanitizePrefix(manualPercentAdjustment),
					manualFlatFeeCharge: manualFlatAdjustmentAmount
				}
			}).unwrap();

			const revisedRevision = response?.revisions?.find((rev) => rev.revisionId === revisionId);

			setRevision(revisedRevision);
			generateAutoCadFile();
			setLineItemMode("other");
		} catch (error) {
			console.error(error);
			window.scrollTo(0, 0);
		}
	};

	const uploadDwgPdfAndSave = async () => {
		const { isValid, dwgFile, pdfFile } = validateDwgPdfFiles(uploadedFiles, quoteId);

		if (!isValid || !dwgFile || !pdfFile || !quoteId) {
			setInvalidUploads(true);
			return;
		}

		try {
			setIsLoading(true);
			setInvalidUploads(false);
			const pdfUpload = await uploadFile(pdfFile, quoteId, revisionId, uploadContainerBlob);
			const dwgUpload = await uploadFile(dwgFile, quoteId, revisionId, uploadContainerBlob);

			await updateRevision({
				quoteId,
				revisionId,
				revision: {
					...revision,
					dwgFileUrl: dwgUpload.fileName,
					pdfFileUrl: pdfUpload.fileName
				}
			}).unwrap();

			navigate(`/custom-quote/${quoteId}`);
		} catch (error) {
			console.error(error);
			window.scrollTo(0, 0);
		} finally {
			setIsLoading(false);
		}
	};

	const updateGlobals = async (configuration: models["PendingOrderConfigurationViewModel"]) => {
		if (!quoteId) return;

		try {
			const revisionResponse = await updateRevision({
				quoteId,
				revisionId,
				revision: {
					...revision,
					globals: configuration.globals
				}
			}).unwrap();

			const updatedRevision = revisionResponse?.revisions?.find((rev) => rev.revisionId === revisionId);

			if (updatedRevision) {
				setRevision(updatedRevision);
			} else {
				// optimistic fallback so UI still updates immediately
				handleSetRevisionGlobals(configuration.globals);
			}
		} catch {
			window.scrollTo(0, 0);
		}
	};

	const handleBaseItemEdit = () => {
		setLineItemMode("base");
	};

	const handleSpecialModEdit = () => {
		setLineItemMode("special");
	};

	useEffect(() => {
		const total = calculateQuoteTotal(
			baseQuoteTotal,
			cqFormValues?.specialMods ?? revision?.specialMods ?? [],
			manualPercentAdjustment,
			manualFlatAdjustment,
			cqComplexityScoreTotal,
			complexityTierConfig
		);
		setTotal(total);
	}, [
		baseQuoteTotal,
		manualPercentAdjustment,
		manualFlatAdjustment,
		cqComplexityScoreTotal,
		complexityTierConfig,
		setTotal,
		cqFormValues?.specialMods,
		revision?.specialMods
	]);

	const handleBaseItemEstimate = async (_: string, lineItem: models["PendingLineItemViewModel"]): Promise<void> => {
		handleSetRevisionLineItem(lineItem);
		await handleGeneratePriceEstimate(
			[
				{
					lineItems: lineItem || customQuoteData?.lineItem ? [lineItem ?? customQuoteData?.lineItem] : []
				}
			],
			revision?.revisionId
		);
		fetchCqComplexityTierConfig();
		window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
	};

	const cancelAndDeleteRevision = async () => {
		openDialog({
			dialogStatus: DialogTracking.DELETE_CUSTOM_QUOTE_REVISION,
			dialogId: quoteId,
			dialogData: revisionId
		});
	};

	return (
		<>
			<AddCustomQuoteWrapper>
				{isLoading ? (
					<Skeleton
						width={97}
						height={24}
						variant="rectangular"
					/>
				) : (
					<LinkButton
						hasBackIcon
						onClick={() => {
							openDialog({ dialogStatus: DialogTracking.LEAVE_ADD_CUSTOM_QUOTE, dialogId: quoteId });
						}}
						data-testid="add-custom-quote-start-over-link"
						sx={{ width: "max-content" }}
					>
						{CustomQuoteRequest}
					</LinkButton>
				)}
				<h1>{AddCustomQuoteText}</h1>
			</AddCustomQuoteWrapper>
			{(updateRevisionError || deleteRevisionError) && (
				<AddCustomQuoteAlert severity="error">
					<AlertTitle>{CantProcessRequestText}</AlertTitle>
					<p>{ServerTemporarilyUnavailableText}</p>
				</AddCustomQuoteAlert>
			)}
			{errorBannerType && errorBannerType.length > 0 && (
				<div>
					<ErrorBanner
						errorMessaging={handlePriceValidationErrorMessaging(
							errorBannerType,
							postError,
							lineItemImportErrors.filter((error) => error.lineItemError?.message !== EditedText).length,
							true
						)}
						isLoading={isValidateLoading}
						ref={errorBannerElement}
					/>
				</div>
			)}
			{!isLoading && <Heading>{CustomQuoteRequestText}</Heading>}
			<Content>
				<Paper elevation={1}>
					<CustomQuoteDetail
						quote={customQuoteData}
						isLoading={isLoading}
					/>
				</Paper>

				{!isLoading && (
					<SelectedBox showBorder={isBaseOrRevise}>
						<AddQuoteLineItemGridContainer>
							<BaseItemEdit>
								<h3>{BaseItemHeaderText}</h3>
								{!isBaseOrRevise && <LinkButton onClick={handleBaseItemEdit}>Edit</LinkButton>}
							</BaseItemEdit>
							<ItemAndModsContainer>
								{!isValidateLoading && !isLoadingCustomQuote ? (
									<>
										{isBaseOrRevise && (
											<RequiredFieldWrapper>{RequiredFieldsText}</RequiredFieldWrapper>
										)}
										{Object.values(lineItemErrors).length > 0 && (
											<ErrorText>
												<HelperText
													text={Object.values(lineItemErrors)}
													isError
												/>
											</ErrorText>
										)}
										<AttributesContainer>
											<GlobalInfo>
												<div data-testid="add-quote-global-attributes">
													{getAttributes({
														globals: revision?.globals ?? customQuoteData?.globals
													})}
												</div>
												<div>
													{getSubheaderAttributes({
														globals: revision?.globals ?? customQuoteData?.globals
													})}
												</div>
											</GlobalInfo>
											{isBaseOrRevise && (
												<GlobalFormProvider isCustomQuoteData={Boolean(customQuoteData)}>
													<AddGlobalAttributes
														isCustomColor={false}
														isUpdate
														isCustomQuote
														handleConfigurationUpdate={updateGlobals}
														customQuoteGlobals={revision?.globals}
													/>
												</GlobalFormProvider>
											)}
										</AttributesContainer>
										{!isBaseOrRevise && (
											<CustomQuoteLineItemGrid
												infoSlot={
													<CustomQuoteItemDetailTitle>
														{BaseItemHeaderText}
													</CustomQuoteItemDetailTitle>
												}
												isListPrice={true}
												mainRowContent={[
													<Header
														key="base-sku-header"
														item={
															addedLineItemWithPrice?.current ?? revision?.lineItem ?? {}
														}
													/>,
													<CustomQuoteItemDetailField key="base-sku-door-style">
														<div>{DoorStyleText}</div>
														<div data-testid="custom-quote-style-description">
															{(addedGlobals.current?.globals ?? revision?.globals)?.style
																?.description ?? UnavailableDataPlaceholderText}
														</div>
													</CustomQuoteItemDetailField>,
													<CustomQuoteItemDetailField key="base-sku-finish">
														<div>{FinishText}</div>
														<div data-testid="custom-quote-finish-description">
															{(addedGlobals.current?.globals ?? revision?.globals)
																?.finish?.description ?? UnavailableDataPlaceholderText}
														</div>
													</CustomQuoteItemDetailField>,
													<CustomQuoteItemDetailField key="base-sku-std-price">
														<div>{StandardListPriceText}</div>
														<div data-testid="custom-quote-std-price">
															{formatPriceWithPrefix(
																addedLineItemWithPrice.current?.listPrice ??
																	revision?.lineItem?.listPrice
															)}
														</div>
													</CustomQuoteItemDetailField>
												]}
												modificationRows={addedLineItemWithPrice.current?.modifications?.map(
													(modification) => (
														<Modification
															description={modification.description}
															key={modification.sku}
															lineNumber={modification.lineItemNumber}
															title={modification.sku}
															values={modification.values}
														/>
													)
												)}
												requiredCategoriesRows={addedLineItemWithPrice.current?.requiredCategories?.map(
													(category) => (
														<Modification
															key={category.categoryName}
															requiredCategory={category}
															lineNumber={undefined}
														/>
													)
												)}
											/>
										)}
										{isBaseOrRevise && !isLoadingCustomQuote && !isFetchingCustomQuote && (
											<StyledAddLineItem
												handleLineItem={handleBaseItemEstimate}
												isCustomQuote
												isAddLineItem
												configuration={{ globals: revision?.globals }}
												productLineId={revision?.globals?.productLine?.id}
												defaultItem={revision?.lineItem}
												onLoadingChange={setIsBaseItemLoading}
											>
												{isBaseItemLoading || isValidateLoading ? (
													<Skeleton
														variant="text"
														height={36}
														width={280}
														data-testid="generate-base-price-estimate-skeleton"
													/>
												) : (
													<Button
														type="submit"
														variant="contained"
														data-testid="generate-base-price-estimate"
														form="add-line-item-form"
													>
														{GenerateBasePriceText}
													</Button>
												)}
											</StyledAddLineItem>
										)}
									</>
								) : (
									<div>
										<ModificationSkeletonContainer>
											<Skeleton height={24} />
											<Skeleton height={24} />
										</ModificationSkeletonContainer>

										<ModificationSkeletonContainer>
											<Skeleton height={24} />
											<Skeleton height={24} />
										</ModificationSkeletonContainer>

										<Skeleton
											height={24}
											width="50%"
										/>
										<Skeleton
											height={24}
											width="50%"
										/>
									</div>
								)}
							</ItemAndModsContainer>
						</AddQuoteLineItemGridContainer>
					</SelectedBox>
				)}

				{lineItemMode !== "base" && !isLoading && (
					<SelectedBox showBorder={isSpecialMode}>
						<AddQuoteLineItemGridContainer>
							<BaseItemEdit>
								<h3>{CQCustomizationsHeaderText}</h3>
								{isOtherMode && <LinkButton onClick={handleSpecialModEdit}>Edit</LinkButton>}
							</BaseItemEdit>
							{isOtherOrRevise && <QuoteCustomizationsSummary revision={revision} />}
							{isSpecialMode && (
								<CQModifications
									revision={revision}
									specialMods={specialModifications ?? []}
									getAutoCADFile={getAutoCADFile}
									baseQuoteTotal={baseQuoteTotal}
									complexityTierConfig={complexityTierConfig}
									onValuesChange={setCqFormValues}
									setTotal={setTotal}
								/>
							)}
						</AddQuoteLineItemGridContainer>
						<Snackbar
							open={snackbarOpen}
							isLoading={dwgData.loading}
							loadingText={GeneratingDWGText}
							successText={DWGDownloadText}
							errorText={DWGErrorText}
							isError={!!dwgData.error}
						/>
					</SelectedBox>
				)}

				{isOtherMode && !isLoading && (
					<>
						<SelectedBox showBorder={lineItemMode === "other"}>
							{invalidUploads && (
								<ErrorText>
									<HelperText
										text={DwgPdfBothRequiredErrorText}
										isError
									/>
								</ErrorText>
							)}
							<AutocadUpload onFilesSelected={handleFilesSelected} />
						</SelectedBox>
						<ChargesContainer isUpcharge={Boolean(manualPercentAdjustment)}>
							<div>
								<span>{BaseItemListPriceText}</span>
								<span>{formatPriceWithPrefix(baseQuoteTotal)}</span>
							</div>
							<div>
								<span>{CustomizationChargeText}</span>
								<span>{formatPriceWithPrefix(customizationChargeAmount)}</span>
							</div>
							<div>
								<span>{ManualPercentAdjustment} </span>
								<span>{formatPriceWithPrefix(manualPercentAdjustmentAmount)}</span>
							</div>
							<div>
								<span>{ManualFlatAdjustment}</span>
								<span>{formatPriceWithPrefix(manualFlatAdjustmentAmount)}</span>
							</div>
							<div>
								<span>{TotalPriceText}</span>
								<span>{formatPriceWithPrefix(total)}</span>
							</div>
						</ChargesContainer>
					</>
				)}
				{!isLoading && (
					<SendQuoteButtonsContainer>
						<Button
							variant="outlined"
							onClick={cancelAndDeleteRevision}
						>
							Cancel Custom Quote
						</Button>
						{uploadedFiles.length > 0 && (
							<SendQuoteButton
								type="submit"
								variant="contained"
								onClick={uploadDwgPdfAndSave}
								endIcon={<SendIcon />}
							>
								{SendQuoteButtonText}
							</SendQuoteButton>
						)}
					</SendQuoteButtonsContainer>
				)}
			</Content>
		</>
	);
};
export default AddQuotePage;
